import React from 'react'
import { Alert, List, Stack } from '@mantine/core'

import { ConditionalScriptBlock } from '@/components/Event/CrForm/components/ConditionalScriptBlock'
import { StateScriptBlock } from '@/components/Event/CrForm/components/StateScriptBlock'
import { TimestampsControl } from '@/components/Event/CrForm/components/TimestampsControl'
import {
  CrFormHandle,
  CrFormStep,
  witnessStepKey,
} from '@/components/Event/CrForm/CrFormStep'
import {
  getScriptValues,
  getStateRecordScript,
} from '@/components/Event/CrForm/stateRecordScripts'
import { Step } from '@/components/Event/CrForm/Step'
import { useFormattedCurrentDateParts } from '@/components/Event/NewScript/hooks/useFormattedCurrentDateParts'
import { Event, Reporter } from '@/types'
import { getEventTimezone } from '@/utils/time'

// Opening section (per witness).
export const OpeningStep: React.FC<{
  event: Event
  reporter: Reporter
  form: CrFormHandle
  witnessIndex: number
}> = ({ event, reporter, form, witnessIndex }) => {
  const dt = useFormattedCurrentDateParts(getEventTimezone(event))
  const announceRecord = form.values.sa?.announce_record ?? null
  // In CA and WA the SA performs the read-on regardless of the Court Reporter's
  // announce preference, so the state script is never gated behind it.
  const stateScript = getStateRecordScript(event)

  return (
    <Step
      id={witnessStepKey(CrFormStep.OPENING, witnessIndex)}
      title="Opening"
      form={form}
      nextStepId={witnessStepKey(CrFormStep.QUESTIONING, witnessIndex)}
    >
      <Stack>
        <Alert color="yellow" title="Reminders">
          <List size="sm" spacing={4}>
            <List.Item>Log every timestamp.</List.Item>
            <List.Item>Spotlight the witness.</List.Item>
          </List>
        </Alert>

        {stateScript ? (
          <StateScriptBlock
            heading={`${stateScript.stateName} read-on`}
            lines={stateScript.opening}
            values={getScriptValues(event, reporter, dt)}
          />
        ) : (
          <ConditionalScriptBlock announceRecord={announceRecord}>
            Good {dt.partOfTheDay}. We are now on the record. Today is{' '}
            {dt.dayOfWeek}, {dt.formattedDate}, and the time is{' '}
            {dt.formattedTimeWithTimezone}. In this proceeding, the licensed
            Court Reporter owns the record and is solely responsible for
            producing the official transcript. I, the Filevine Session
            Assistant, am present to provide technical and logistical support
            only.
            <br />
            <br />
            Court Reporter, you may proceed.
          </ConditionalScriptBlock>
        )}

        <TimestampsControl
          event={event}
          form={form}
          witnessIndex={witnessIndex}
        />
      </Stack>
    </Step>
  )
}
